import React from 'react';
import { getImageUrl } from "../../helper/imageHelper";

const ActivityLogCard = ({ log }) => {
    const isLogin = log.action === 'LOGIN';
    const isUpdate = log.action.includes('UPDATE');
    const isDelete = log.action.includes('DELETE');
    const isCreate = log.action.includes('CREATE') || log.action.includes('REGISTER');

    let badgeColor = "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    if (isLogin) badgeColor = "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
    if (isUpdate) badgeColor = "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400";
    if (isDelete) badgeColor = "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    if (isCreate) badgeColor = "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";

    // Try to parse details if it looks like JSON, otherwise keep as string
    let detailsContent = log.details;
    let isJsonDetails = false;
    try {
        if (typeof log.details === 'string' && (log.details.startsWith('{') || log.details.startsWith('['))) {
            const parsed = JSON.parse(log.details);
            detailsContent = JSON.stringify(parsed, null, 2);
            isJsonDetails = true;
        }
    } catch (e) {
        // Not JSON
    }

    // Prepare "Technical" view data
    // If we have IP, we can simulate a JSON object for the "Changes" block if it's a simple action like Login
    let changesBlock = null;
    if (isJsonDetails) {
        changesBlock = detailsContent;
    } else if (log.ip_address) {
        // Create a synthetic JSON for display if we have IP but simple details
        changesBlock = JSON.stringify({
            ip: log.ip_address,
            details: log.details
        }, null, 2);
    }

    return (
        <div className="flex flex-col rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100 dark:bg-[#1e293b] dark:ring-gray-800">
            {/* Header: User Info & Action Badge */}
            <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <img
                        loading="lazy"
                        src={getImageUrl(log.user_avatar) || "/default-avatar.png"}
                        alt={log.user_name}
                        className="h-10 w-10 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
                    />
                    <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">{log.user_name}</h4>
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400">
                            {/* Displaying ID loosely to match screenshot "2401:..." style if widely available, else just a placeholder ID or partial hash */}
                            User ID: {log.user_id || 'N/A'}
                        </p>
                    </div>
                </div>
                <span className={`rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${badgeColor}`}>
                    {log.action}
                </span>
            </div>

            {/* Body: Action Title & Date */}
            <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">
                    {log.details && !isJsonDetails ? log.details : log.action.replace(/_/g, ' ')}
                </h3>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>{new Date(log.created_at).toLocaleString()}</span>
                    <span>&bull;</span>
                    {/* Placeholder for "User #1" tags from screenshot */}
                    <span className="flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
                            <path d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z" />
                        </svg>
                        User #{log.user_id}
                    </span>
                </div>
            </div>

            {/* Code/Changes Block */}
            <div className="mt-auto rounded-lg bg-gray-50 p-3 pt-3 dark:bg-[#0f172a]">
                <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    Changes:
                </p>
                <div className="relative">
                    <pre className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                        {changesBlock || "{}"}
                    </pre>
                </div>
            </div>

            {/* Footer: Tech Details (User Agent etc placeholder) */}
            <div className="mt-3 truncate text-xs text-gray-400 dark:text-gray-500">
                IP: {log.ip_address}
            </div>
        </div>
    );
};

export default ActivityLogCard;
